import { type FormEvent, useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { etiqueta, fechaCorta } from '../formato';
import { usePuede } from '../permisos';
import type { CatalogosLote, Lote, Vehiculo } from '../paginas/tipos';
import type { Subasta } from './Compra';

const CLASE_AVISO = { alto: 'peligro', medio: 'aviso', info: '', oportunidad: 'exito' } as const;
const v = (f: FormData, k: string) => String(f.get(k) ?? '').trim();
const nulo = (s: string) => (s ? s : null);
const num = (s: string) => (s ? Number(s) : null);

/** Avisos del lote (tabla «Datos del lote y compra» de la arquitectura 2.15). */
export function AvisosLote({ lote }: { lote: Lote }) {
  if (!lote.avisos.length)
    return <p className="subtitulo">Sin avisos: los datos del lote no disparan ninguna alerta.</p>;
  return (
    <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
      {lote.avisos.map((a) => (
        <li key={a.clave}>
          <span className={`etiqueta ${CLASE_AVISO[a.nivel]}`}>{a.nivel}</span> {a.texto}
        </li>
      ))}
    </ul>
  );
}

/**
 * Lote y checklist (arquitectura 2.15, sección 5): los datos del lote o de la compra directa
 * capturados a mano (nada de scraping), el broker, el medio de pago y lo que solo el lotero puede
 * saber: historial de título con evidencia, inspección física y cotización de reparación.
 */
export function LoteVehiculo({ v: auto, alGuardar }: { v: Vehiculo; alGuardar: () => void }) {
  const api = useApi();
  const permitido = usePuede();
  const editable = permitido('compras.registrar');
  const lote = useConsulta<Lote>(`/sourcing/lotes/${auto.id}`);
  const { datos: catalogos } = useConsulta<CatalogosLote>('/sourcing/subastas/catalogos');
  const { datos: subastas } = useConsulta<Subasta[]>('/sourcing/subastas');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const sinLote = lote.estadoError === 404;
  const l = lote.datos;

  const hacer = async (fn: () => Promise<unknown>, mensaje: string) => {
    setError(null);
    setAviso(null);
    try {
      await fn();
      setAviso(mensaje);
      lote.recargar();
      alGuardar();
      setEditando(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const datosDe = (f: FormData) => ({
    tituloTipo: v(f, 'tituloTipo') || 'desconocido',
    motor: v(f, 'motor') || 'desconocido',
    videoArranque: nulo(v(f, 'videoArranque')),
    danoPrimario: nulo(v(f, 'danoPrimario')),
    danoSecundario: nulo(v(f, 'danoSecundario')),
    odometro: num(v(f, 'odometro')),
    unidadOdometro: v(f, 'unidadOdometro') || 'mi',
    odometroReal: v(f, 'odometroReal') === '' ? null : v(f, 'odometroReal') === 'si',
    tipoVenta: nulo(v(f, 'tipoVenta')),
    fotos: num(v(f, 'fotos')),
    ciudad: nulo(v(f, 'ciudad')),
    estadoEeuu: nulo(v(f, 'estadoEeuu').toUpperCase()),
    fechaSubasta: nulo(v(f, 'fechaSubasta')),
  });
  const brokerDe = (f: FormData) => {
    const nombre = v(f, 'brokerNombre');
    if (!nombre) return null;
    const calculo = v(f, 'brokerCalculo') || 'fijo';
    return {
      nombre,
      fee:
        calculo === 'fijo'
          ? { calculo: 'fijo', monto: v(f, 'brokerMonto') || '0' }
          : {
              calculo: 'porcentaje',
              porcentaje: v(f, 'brokerMonto') || '0',
              ...(v(f, 'brokerMinimo') && { minimo: v(f, 'brokerMinimo') }),
            },
      ...(v(f, 'brokerDeposito') && { depositoUsd: v(f, 'brokerDeposito') }),
    };
  };

  const registrar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const origen = v(f, 'origen') || 'subasta';
    void hacer(
      () =>
        api('POST', '/sourcing/lotes', {
          vehiculoId: auto.id,
          vin: auto.vin,
          origen,
          ...(origen === 'subasta' && { subasta: v(f, 'subasta'), numeroLote: v(f, 'numeroLote') }),
          ...(v(f, 'urlLote') && { urlLote: v(f, 'urlLote') }),
          ...(v(f, 'vendedor') && { vendedor: v(f, 'vendedor') }),
          datos: datosDe(f),
          broker: brokerDe(f),
          medioPago: nulo(v(f, 'medioPago')),
          ruta: v(f, 'ruta') || 'mexico',
          region: v(f, 'region') || 'resto',
        }),
      'Lote registrado',
    );
  };
  const cambiar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void hacer(
      () =>
        api('PATCH', `/sourcing/lotes/${auto.id}`, {
          datos: datosDe(f),
          broker: brokerDe(f),
          medioPago: nulo(v(f, 'medioPago')),
          vendedor: nulo(v(f, 'vendedor')),
          ruta: v(f, 'ruta') || 'mexico',
          region: v(f, 'region') || 'resto',
        }),
      'Lote actualizado',
    );
  };
  const checklist = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void hacer(
      () =>
        api('PATCH', `/sourcing/lotes/${auto.id}`, {
          historial: v(f, 'historialResultado')
            ? {
                resultado: v(f, 'historialResultado'),
                fuente: v(f, 'historialFuente') || 'otra',
                fecha: v(f, 'historialFecha') || new Date().toISOString().slice(0, 10),
                documentoId: nulo(v(f, 'historialDocumento')),
                nota: nulo(v(f, 'historialNota')),
              }
            : null,
          inspeccion: v(f, 'inspeccionFecha')
            ? {
                fecha: v(f, 'inspeccionFecha'),
                inspector: nulo(v(f, 'inspector')),
                costo: v(f, 'inspeccionCosto')
                  ? { cantidad: v(f, 'inspeccionCosto'), moneda: v(f, 'inspeccionMoneda') || 'USD' }
                  : null,
                nota: null,
              }
            : null,
          reparacion: v(f, 'reparacionMxn')
            ? {
                cantidadMxn: v(f, 'reparacionMxn'),
                fuente: v(f, 'reparacionFuente') || 'manual',
                bolsasAire: f.get('bolsasAire') === 'on',
                referencia: null,
              }
            : null,
        }),
      'Checklist guardado',
    );
  };

  const opciones = (
    lista: string[] | undefined,
    actual: string | null | undefined,
    vacio = '—',
  ) => (
    <>
      <option value="">{vacio}</option>
      {(lista ?? []).map((c) => (
        <option key={c} value={c} selected={c === actual}>
          {etiqueta(c)}
        </option>
      ))}
    </>
  );
  const d = l?.datos;
  const formularioDatos = (
    <>
      <label className="campo">
        <span>Tipo de título</span>
        <select name="tituloTipo" defaultValue={d?.tituloTipo ?? 'desconocido'}>
          {(catalogos?.titulos ?? []).map((c) => (
            <option key={c} value={c}>
              {etiqueta(c)}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        <span>Motor</span>
        <select name="motor" defaultValue={d?.motor ?? 'desconocido'}>
          {(catalogos?.motores ?? []).map((c) => (
            <option key={c} value={c}>
              {etiqueta(c)}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        <span>Daño primario</span>
        <select name="danoPrimario" defaultValue={d?.danoPrimario ?? ''}>
          {opciones(catalogos?.danos, undefined)}
        </select>
      </label>
      <label className="campo">
        <span>Daño secundario</span>
        <select name="danoSecundario" defaultValue={d?.danoSecundario ?? ''}>
          {opciones(catalogos?.danos, undefined)}
        </select>
      </label>
      <label className="campo">
        <span>Odómetro</span>
        <input name="odometro" type="number" min={0} defaultValue={d?.odometro ?? ''} />
      </label>
      <label className="campo">
        <span>Unidad</span>
        <select name="unidadOdometro" defaultValue={d?.unidadOdometro ?? 'mi'}>
          <option value="mi">millas</option>
          <option value="km">km</option>
        </select>
      </label>
      <label className="campo">
        <span>Odómetro real</span>
        <select
          name="odometroReal"
          defaultValue={
            d?.odometroReal === null || d?.odometroReal === undefined
              ? ''
              : d.odometroReal
                ? 'si'
                : 'no'
          }
        >
          <option value="">—</option>
          <option value="si">Actual</option>
          <option value="no">Not Actual</option>
        </select>
      </label>
      <label className="campo">
        <span>Tipo de venta</span>
        <select name="tipoVenta" defaultValue={d?.tipoVenta ?? ''}>
          {opciones(catalogos?.tiposVenta, undefined)}
        </select>
      </label>
      <label className="campo">
        <span>Fotos del lote</span>
        <input name="fotos" type="number" min={0} defaultValue={d?.fotos ?? ''} />
      </label>
      <label className="campo">
        <span>Ciudad</span>
        <input name="ciudad" defaultValue={d?.ciudad ?? ''} maxLength={80} />
      </label>
      <label className="campo">
        <span>Estado (EE. UU.)</span>
        <input
          name="estadoEeuu"
          defaultValue={d?.estadoEeuu ?? ''}
          maxLength={2}
          placeholder="TX"
          style={{ textTransform: 'uppercase' }}
        />
      </label>
      <label className="campo">
        <span>Fecha de subasta</span>
        <input name="fechaSubasta" type="date" defaultValue={d?.fechaSubasta ?? ''} />
      </label>
      <label className="campo">
        <span>Video de arranque (liga)</span>
        <input name="videoArranque" type="url" defaultValue={d?.videoArranque ?? ''} />
      </label>
      <label className="campo">
        <span>Medio de pago</span>
        <select name="medioPago" defaultValue={l?.medioPago ?? ''}>
          {opciones(catalogos?.mediosPago, undefined)}
        </select>
      </label>
      <label className="campo">
        <span>Ruta del auto</span>
        <select name="ruta" defaultValue={l?.ruta ?? auto.ruta}>
          <option value="mexico">Importar a México</option>
          <option value="eeuu">Se queda en EE. UU.</option>
          <option value="deshuesar">Solo partes</option>
        </select>
      </label>
      <label className="campo">
        <span>Región de destino</span>
        <select name="region" defaultValue={l?.region ?? 'resto'}>
          <option value="frontera">Región fronteriza</option>
          <option value="resto">Resto del país</option>
        </select>
      </label>
      <label className="campo">
        <span>Broker (nombre)</span>
        <input name="brokerNombre" defaultValue={l?.broker?.nombre ?? ''} maxLength={120} />
      </label>
      <label className="campo">
        <span>Fee del broker</span>
        <select name="brokerCalculo" defaultValue={l?.broker?.fee.calculo ?? 'fijo'}>
          <option value="fijo">Fijo (USD)</option>
          <option value="porcentaje">% del martillo</option>
        </select>
      </label>
      <label className="campo">
        <span>Monto o porcentaje</span>
        <input
          name="brokerMonto"
          inputMode="decimal"
          defaultValue={l?.broker?.fee.monto ?? l?.broker?.fee.porcentaje ?? ''}
        />
      </label>
      <label className="campo">
        <span>Mínimo del fee (USD)</span>
        <input name="brokerMinimo" inputMode="decimal" defaultValue={l?.broker?.fee.minimo ?? ''} />
      </label>
      <label className="campo">
        <span>Depósito con el broker (USD)</span>
        <input
          name="brokerDeposito"
          inputMode="decimal"
          defaultValue={l?.broker?.depositoUsd ?? ''}
        />
      </label>
    </>
  );

  return (
    <section className="seccion">
      <header>
        <h2>Lote y checklist</h2>
        <div style={{ flex: 1 }} />
        {l && editable && (
          <button className="boton" onClick={() => setEditando((x) => !x)}>
            {editando ? 'Cancelar' : 'Editar datos del lote'}
          </button>
        )}
      </header>
      {error && (
        <p className="error" style={{ margin: 16 }}>
          {error}
        </p>
      )}
      {aviso && (
        <p className="etiqueta exito" style={{ margin: 16 }}>
          {aviso}
        </p>
      )}
      {lote.cargando && <p className="vacio">Cargando…</p>}
      {sinLote && !editable && <p className="vacio">Sin datos del lote.</p>}
      {sinLote && editable && (
        <form className="cuerpo formulario" onSubmit={registrar}>
          <label className="campo">
            <span>Origen</span>
            <select name="origen" defaultValue="subasta">
              {(catalogos?.origenes ?? ['subasta']).map((c) => (
                <option key={c} value={c}>
                  {etiqueta(c)}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Subasta</span>
            <select name="subasta" defaultValue="copart">
              {(subastas ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Número de lote</span>
            <input name="numeroLote" maxLength={40} />
          </label>
          <label className="campo">
            <span>Liga del lote (solo referencia)</span>
            <input name="urlLote" type="url" />
          </label>
          <label className="campo">
            <span>Vendedor (compra directa)</span>
            <input name="vendedor" maxLength={160} />
          </label>
          {formularioDatos}
          <div className="acciones">
            <button className="boton primario">Registrar lote</button>
          </div>
        </form>
      )}
      {l && !editando && (
        <div className="cuerpo" style={{ display: 'grid', gap: 12 }}>
          <dl className="resumen">
            {(
              [
                [
                  'Origen',
                  `${etiqueta(l.origen)}${l.subasta ? ` · ${subastas?.find((s) => s.id === l.subasta)?.nombre ?? l.subasta}` : ''}${l.numeroLote ? ` · lote ${l.numeroLote}` : ''}`,
                ],
                ['Vendedor', l.vendedor],
                ['Título', etiqueta(l.datos.tituloTipo)],
                ['Motor', etiqueta(l.datos.motor)],
                [
                  'Daño',
                  [l.datos.danoPrimario, l.datos.danoSecundario]
                    .filter(Boolean)
                    .map((x) => etiqueta(x))
                    .join(' + '),
                ],
                [
                  'Odómetro',
                  l.datos.odometro !== null
                    ? `${l.datos.odometro.toLocaleString('es-MX')} ${l.datos.unidadOdometro}${l.datos.odometroReal === false ? ' (Not Actual)' : l.datos.odometroReal ? ' (Actual)' : ''}`
                    : null,
                ],
                ['Tipo de venta', etiqueta(l.datos.tipoVenta)],
                ['Fotos', l.datos.fotos?.toString() ?? null],
                ['Ubicación', [l.datos.ciudad, l.datos.estadoEeuu].filter(Boolean).join(', ')],
                [
                  'Fecha de subasta',
                  l.datos.fechaSubasta ? fechaCorta(l.datos.fechaSubasta) : null,
                ],
                ['Aduana sugerida', l.aduanaSugerida],
                [
                  'Ruta',
                  `${etiqueta(l.ruta === 'mexico' ? 'Importar a México' : l.ruta === 'eeuu' ? 'Se queda en EE. UU.' : 'Solo partes')} · ${etiqueta(l.region)}`,
                ],
                [
                  'Broker',
                  l.broker
                    ? `${l.broker.nombre} · ${l.broker.fee.calculo === 'fijo' ? `USD ${l.broker.fee.monto}` : `${l.broker.fee.porcentaje} %${l.broker.fee.minimo ? ` (mín. ${l.broker.fee.minimo})` : ''}`}${l.broker.depositoUsd ? ` · depósito USD ${l.broker.depositoUsd}` : ''}`
                    : null,
                ],
                ['Medio de pago', etiqueta(l.medioPago)],
                [
                  'Historial de título',
                  l.historial
                    ? `${etiqueta(l.historial.resultado)} según ${etiqueta(l.historial.fuente)} (${fechaCorta(l.historial.fecha)})${l.historial.documentoId ? ' · con evidencia' : ' · sin evidencia'}`
                    : null,
                ],
                [
                  'Inspección',
                  l.inspeccion
                    ? `${fechaCorta(l.inspeccion.fecha)}${l.inspeccion.inspector ? ` por ${l.inspeccion.inspector}` : ''}`
                    : null,
                ],
                [
                  'Reparación cotizada',
                  l.reparacion
                    ? `${l.reparacion.cantidadMxn} MXN por ${etiqueta(l.reparacion.fuente)}${l.reparacion.bolsasAire ? ' · bolsas de aire aparte' : ''}`
                    : null,
                ],
              ] as const
            ).map(([dt, dd]) => (
              <div key={dt}>
                <dt>{dt}</dt>
                <dd>{dd || '—'}</dd>
              </div>
            ))}
          </dl>
          {l.urlLote && (
            <p className="subtitulo">
              <a href={l.urlLote} target="_blank" rel="noreferrer">
                Ver el lote en la subasta
              </a>{' '}
              (la plataforma nunca lo abre por ti)
            </p>
          )}
          <AvisosLote lote={l} />
        </div>
      )}
      {l && editando && (
        <form className="cuerpo formulario" onSubmit={cambiar}>
          <label className="campo">
            <span>Vendedor</span>
            <input name="vendedor" defaultValue={l.vendedor ?? ''} maxLength={160} />
          </label>
          {formularioDatos}
          <div className="acciones">
            <button className="boton primario">Guardar</button>
          </div>
        </form>
      )}
      {l && editable && (
        <form
          className="cuerpo formulario"
          onSubmit={checklist}
          style={{ borderTop: '1px solid var(--borde-suave)' }}
        >
          <h3 style={{ gridColumn: '1 / -1', margin: 0 }}>Lo que solo tú puedes saber</h3>
          <label className="campo">
            <span>Historial de título</span>
            <select name="historialResultado" defaultValue={l.historial?.resultado ?? ''}>
              {opciones(catalogos?.resultadosHistorial, undefined, 'Sin capturar')}
            </select>
          </label>
          <label className="campo">
            <span>Fuente</span>
            <select name="historialFuente" defaultValue={l.historial?.fuente ?? 'nmvtis'}>
              {(catalogos?.fuentesHistorial ?? []).map((c) => (
                <option key={c} value={c}>
                  {etiqueta(c)}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Fecha del reporte</span>
            <input name="historialFecha" type="date" defaultValue={l.historial?.fecha ?? ''} />
          </label>
          <label className="campo">
            <span>Evidencia (id del documento subido)</span>
            <input
              name="historialDocumento"
              defaultValue={l.historial?.documentoId ?? ''}
              placeholder="Sube el reporte en Documentos y pega su id"
            />
          </label>
          <label className="campo">
            <span>Inspección física (fecha)</span>
            <input name="inspeccionFecha" type="date" defaultValue={l.inspeccion?.fecha ?? ''} />
          </label>
          <label className="campo">
            <span>Inspector</span>
            <input name="inspector" defaultValue={l.inspeccion?.inspector ?? ''} maxLength={120} />
          </label>
          <label className="campo">
            <span>Costo de la inspección</span>
            <input
              name="inspeccionCosto"
              inputMode="decimal"
              defaultValue={l.inspeccion?.costo?.cantidad ?? ''}
            />
          </label>
          <label className="campo">
            <span>Moneda</span>
            <select name="inspeccionMoneda" defaultValue={l.inspeccion?.costo?.moneda ?? 'USD'}>
              <option value="USD">USD</option>
              <option value="MXN">MXN</option>
            </select>
          </label>
          <label className="campo">
            <span>Reparación cotizada (MXN)</span>
            <input
              name="reparacionMxn"
              inputMode="decimal"
              defaultValue={l.reparacion?.cantidadMxn ?? ''}
            />
          </label>
          <label className="campo">
            <span>Fuente de la cotización</span>
            <select name="reparacionFuente" defaultValue={l.reparacion?.fuente ?? 'taller'}>
              {(catalogos?.fuentesReparacion ?? []).map((c) => (
                <option key={c} value={c}>
                  {etiqueta(c)}
                </option>
              ))}
            </select>
          </label>
          <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
            <input
              name="bolsasAire"
              type="checkbox"
              defaultChecked={l.reparacion?.bolsasAire ?? false}
              style={{ minHeight: 0 }}
            />
            <span>Bolsas de aire o módulo SRS aparte</span>
          </label>
          <div className="acciones">
            <button className="boton">Guardar checklist</button>
          </div>
        </form>
      )}
    </section>
  );
}
